import { Button } from "@/components/ui/button";
import { Check, ChevronDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PackageItem {
  id: string;
  name: string;
  description: string | null;
  price: string | null;
  features: string[] | null;
  terms: string[] | null;
  terms_label: string | null;
  terms_enabled: boolean;
  highlighted: boolean;
  display_order: number;
}

interface PackagesSettings {
  title: string;
  subtitle: string;
  popular_badge_text: string;
  cta_button_text: string;
  cta_waitlist_text: string;
  show_comparison_link: boolean;
}

interface PackagesProps {
  showPricing: boolean;
  onCtaClick: (packageName?: string) => void;
}

export const Packages = ({ showPricing, onCtaClick }: PackagesProps) => {
  const [openTerms, setOpenTerms] = useState<string | null>(null);
  const [packages, setPackages] = useState<PackageItem[]>([]);
  const [settings, setSettings] = useState<PackagesSettings>({
    title: "Simpl poslovni model",
    subtitle: "Izberi paket, ki ustreza tvoji fazi rasti",
    popular_badge_text: "Najbolj priljubljeno",
    cta_button_text: "Rezerviraj brezplačni posvet",
    cta_waitlist_text: "Pridruži se",
    show_comparison_link: true,
  });
  const [loading, setLoading] = useState(true);
  const [minCardHeight, setMinCardHeight] = useState<number | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, settingsRes] = await Promise.all([
        supabase
          .from("cms_packages")
          .select("*")
          .eq("active", true)
          .order("display_order", { ascending: true }),
        supabase.from("cms_packages_settings").select("*").single(),
      ]);

      if (packagesRes.error) throw packagesRes.error;
      setPackages(packagesRes.data || []);

      if (settingsRes.data) {
        setSettings({
          title: settingsRes.data.title,
          subtitle: settingsRes.data.subtitle,
          popular_badge_text: settingsRes.data.popular_badge_text,
          cta_button_text: settingsRes.data.cta_button_text,
          cta_waitlist_text: settingsRes.data.cta_waitlist_text,
          show_comparison_link: settingsRes.data.show_comparison_link,
        });
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  const measureClosedCardHeights = useCallback(() => {
    // Only enforce equal heights on desktop.
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    if (!isDesktop) {
      setMinCardHeight(null);
      return;
    }

    // Use requestIdleCallback to avoid forced reflow during critical rendering
    const measureFn = () => {
      const heights = cardRefs.current
        .filter((ref): ref is HTMLDivElement => ref !== null)
        .map((ref) => ref.offsetHeight); // offsetHeight is cached, less expensive than getBoundingClientRect

      if (heights.length > 0) {
        setMinCardHeight(Math.max(...heights));
      }
    };

    if ('requestIdleCallback' in window) {
      (window as Window).requestIdleCallback(measureFn, { timeout: 100 });
    } else {
      // Fallback for Safari
      setTimeout(measureFn, 0);
    }
  }, []);

  useEffect(() => {
    // Measure heights ONLY when accordions are closed and we don't have a height yet
    if (loading || packages.length === 0) return;
    if (openTerms !== null) return;
    if (minCardHeight !== null) return; // Don't re-measure if we already have a height

    measureClosedCardHeights();
  }, [loading, packages.length, showPricing, openTerms, minCardHeight, measureClosedCardHeights]);

  // Recalculate on window resize (debounced)
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (openTerms === null) {
          setMinCardHeight(null);
        }
      }, 150);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, [openTerms]);

  // Calculate max features count for alignment
  const maxFeatures = Math.max(...packages.map((pkg) => pkg.features?.length || 0), 0);

  // Pad features arrays to match max length for visual alignment
  const getAlignedFeatures = (features: string[] | null): (string | null)[] => {
    const featureList = features || [];
    const padded: (string | null)[] = [...featureList];
    while (padded.length < maxFeatures) {
      padded.push(null);
    }
    return padded;
  };

  if (loading) {
    return (
      <section className="py-24 md:py-32 bg-background">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center mb-16">
            <div className="h-10 bg-muted rounded animate-pulse mb-6 max-w-md mx-auto" />
            <div className="h-6 bg-muted rounded animate-pulse max-w-lg mx-auto" />
          </div>
          <div className="grid gap-8 md:grid-cols-3 mx-auto max-w-6xl">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border bg-card p-8 h-96 animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (packages.length === 0) {
    return null;
  }

  return (
    <section className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="text-4xl font-bold mb-6 text-foreground md:text-5xl">
            {settings.title}
          </h2>
          <p className="text-xl text-muted-foreground mb-4">{settings.subtitle}</p>
          {settings.show_comparison_link && (
            <Link 
              to="/paketi" 
              className="inline-flex items-center gap-2 text-secondary hover:text-secondary/80 font-medium transition-colors"
            >
              Primerjaj vse pakete
              <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <div className="grid gap-8 md:grid-cols-3 mx-auto max-w-6xl items-start">
          {packages.map((pkg, index) => {
            const currentTermsCount = pkg.terms?.filter((t) => t).length || 0;
            const hasTerms = currentTermsCount > 0 && (pkg.terms_enabled ?? true);

            return (
              <div key={pkg.id} className="relative">
                {pkg.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 inline-block rounded-full bg-secondary px-4 py-1.5 text-sm font-bold text-secondary-foreground whitespace-nowrap">
                    {settings.popular_badge_text}
                  </div>
                )}

                <div
                  ref={(el) => (cardRefs.current[index] = el)}
                  style={{ 
                    minHeight: minCardHeight && openTerms !== pkg.id ? `${minCardHeight}px` : undefined,
                    height: openTerms === pkg.id ? 'auto' : undefined
                  }}
                  className={`rounded-2xl border bg-card p-8 transition-all flex flex-col ${
                    pkg.highlighted ? "border-secondary/50 shadow-lg" : "border-border shadow-sm"
                  }`}
                >
                  <h3 className="text-2xl font-bold text-foreground mb-2">{pkg.name}</h3>

                  <p className="text-muted-foreground mb-6 text-lg">{pkg.description}</p>

                  {showPricing && pkg.price && (
                    <div className="mb-8">
                      <span className="text-5xl font-bold text-foreground">{pkg.price}</span>
                      <span className="text-muted-foreground text-lg">/mesec</span>
                    </div>
                  )}

                  <ul className="mb-8 space-y-4 flex-1">
                    {getAlignedFeatures(pkg.features).map((feature, idx) =>
                      feature ? (
                        <li key={idx} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-accent mt-0.5 shrink-0" />
                          <span className="text-foreground text-base">{feature}</span>
                        </li>
                      ) : (
                        <li key={idx} className="flex items-start gap-3 invisible">
                          <Check className="h-5 w-5 text-accent mt-0.5 shrink-0" />
                          <span className="text-foreground text-base">&nbsp;</span>
                        </li>
                      )
                    )}
                  </ul>

                  <div className="mt-auto">
                    <Button
                      onClick={() => onCtaClick(pkg.name)}
                      className={
                        pkg.highlighted
                          ? "w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 text-base rounded-full"
                          : "w-full h-12 text-base rounded-full"
                      }
                    >
                      {showPricing ? settings.cta_button_text : settings.cta_waitlist_text}
                    </Button>

                    {hasTerms && (
                      <Collapsible
                        key={`terms-${pkg.id}`}
                        open={openTerms === pkg.id}
                        onOpenChange={() => setOpenTerms(openTerms === pkg.id ? null : pkg.id)}
                        className="mt-4"
                      >
                        <CollapsibleTrigger 
                          className="flex items-center gap-2 text-sm text-muted-foreground transition-colors h-5 hover:text-foreground cursor-pointer"
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              openTerms === pkg.id ? "rotate-180" : ""
                            }`}
                          />
                          <span>{pkg.terms_label || "Pravila obsega dela"}</span>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-3">
                          <ul className="space-y-2 text-xs text-muted-foreground italic pl-6">
                            {pkg.terms!
                              .filter((term) => term)
                              .map((term, idx) => (
                                <li key={idx} className="list-disc">
                                  {term}
                                </li>
                              ))}
                          </ul>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
